const CARD_WIDTH = 1600
const FOOTER_HEIGHT = 150
const SUN = '#f0c27a'
const DEEP = '#13241c'
const MIST = '#e7f0e8'

export interface PostcardInput {
  /** The live WebGL canvas. Must be read in the same tick as the render. */
  source: HTMLCanvasElement
  placeName: string
  subject: string
  caption: string
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

export function composePostcard(input: PostcardInput): HTMLCanvasElement {
  const scale = CARD_WIDTH / input.source.width
  const imageHeight = Math.round(input.source.height * scale)
  const card = document.createElement('canvas')
  card.width = CARD_WIDTH
  card.height = imageHeight + FOOTER_HEIGHT
  const ctx = card.getContext('2d')!

  ctx.drawImage(input.source, 0, 0, CARD_WIDTH, imageHeight)

  const badge = `${input.placeName} · Costa Rica`
  ctx.font = '600 30px "Source Sans 3", system-ui, sans-serif'
  const badgeWidth = ctx.measureText(badge).width + 56
  ctx.fillStyle = 'rgba(19, 36, 28, 0.72)'
  roundedRect(ctx, 40, 40, badgeWidth, 64, 32)
  ctx.fill()
  ctx.fillStyle = SUN
  ctx.textBaseline = 'middle'
  ctx.fillText(badge, 68, 74)

  ctx.fillStyle = DEEP
  ctx.fillRect(0, imageHeight, CARD_WIDTH, FOOTER_HEIGHT)
  ctx.fillStyle = SUN
  ctx.fillRect(0, imageHeight, CARD_WIDTH, 4)

  const footerMid = imageHeight + FOOTER_HEIGHT / 2
  ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = MIST
  ctx.font = '700 46px Fraunces, Georgia, serif'
  ctx.fillText(input.subject, 56, footerMid + 2)
  ctx.fillStyle = 'rgba(231, 240, 232, 0.68)'
  ctx.font = '400 27px "Source Sans 3", system-ui, sans-serif'
  ctx.fillText(input.caption, 56, footerMid + 42)

  ctx.textAlign = 'right'
  ctx.fillStyle = SUN
  ctx.font = '600 28px "Source Sans 3", system-ui, sans-serif'
  ctx.fillText('Play Costa Rica', CARD_WIDTH - 56, footerMid + 2)
  ctx.fillStyle = 'rgba(231, 240, 232, 0.55)'
  ctx.font = '400 24px "Source Sans 3", system-ui, sans-serif'
  ctx.fillText(
    new Date().toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }),
    CARD_WIDTH - 56,
    footerMid + 40,
  )

  return card
}

export function canvasToBlob(card: HTMLCanvasElement, quality = 0.92): Promise<Blob | null> {
  return new Promise((resolve) => card.toBlob(resolve, 'image/jpeg', quality))
}

function toBlob(card: HTMLCanvasElement): Promise<Blob | null> {
  return canvasToBlob(card)
}

export interface RenderedThumbnail {
  blob: Blob
  width: number
  height: number
}

/** Atlas rows need a cheap image; the full postcard is kept separately. */
export async function renderThumbnail(
  source: HTMLCanvasElement,
  maxWidth = 480,
): Promise<RenderedThumbnail | null> {
  const scale = Math.min(1, maxWidth / source.width)
  const width = Math.max(1, Math.round(source.width * scale))
  const height = Math.max(1, Math.round(source.height * scale))
  const thumb = document.createElement('canvas')
  thumb.width = width
  thumb.height = height
  thumb.getContext('2d')!.drawImage(source, 0, 0, width, height)
  const blob = await canvasToBlob(thumb, 0.72)
  return blob ? { blob, width, height } : null
}

export type PostcardOutcome = 'shared' | 'downloaded' | 'failed'

export async function sharePhotoBlob(
  blob: Blob,
  filename: string,
  title: string,
): Promise<PostcardOutcome> {
  const file = new File([blob], filename, { type: blob.type || 'image/jpeg' })

  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title })
      return 'shared'
    } catch {
      // Falls through to download so a canceled sheet still leaves the photo.
    }
  }

  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  // Revoking immediately can cancel the download in some browsers.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000)
  return 'downloaded'
}

export async function sharePostcard(
  card: HTMLCanvasElement,
  filename: string,
  title: string,
): Promise<PostcardOutcome> {
  const blob = await toBlob(card)
  if (!blob) return 'failed'
  return sharePhotoBlob(blob, filename, title)
}

export function slugify(text: string): string {
  return (
    text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'view'
  )
}
